import { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useParams, useOutletContext } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { lang } from "config"
import Input from "components/forms/input"
import ErrorMessage from "components/forms/error-message"
import SelectSearchable from "components/forms/select-searchable"
import { useAuth } from "contexts/auth-context"
import { getBranchList, getGenericSupplier } from "helpers/general-helper"
import { loadMasterUomFactors, masterUomFactor } from "helpers/uom-helper"
import { Plus } from "components/icons/outline"

function NonPoProductPicker({ disabled, outletId, onPick }) {
  const inventoryApi = process.env.REACT_APP_INVENTORY_API_URL
  const perPage = 30

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [page, setPage] = useState(1)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const debounceRef = useRef()

  const fetchPage = async (targetPage = 1, append = false) => {
    if (!inventoryApi || !outletId) return
    setLoading(true)
    try {
      const url = new URL(`${inventoryApi}/ps_list`)
      const params = {
        page: targetPage,
        per_page: perPage,
        search: query,
        outlet_id: outletId,
        only_in_stock: false,
        with_attributes: false,
      }
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v)
      })

      const res = await fetch(url.toString())
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      const rows = Array.isArray(json?.data) ? json.data : []
      setItems((prev) => (append ? [...prev, ...rows] : rows))
      setHasMore(Boolean(json?.meta?.next_page_url) || rows.length === perPage)
      setPage(targetPage)
    } catch {
      toast.error("Gagal mengambil daftar produk.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open || !outletId) return
    fetchPage(1, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, outletId])

  useEffect(() => {
    if (!open) return
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchPage(1, false)
    }, 300)
    return () => clearTimeout(debounceRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const onScroll = (event) => {
    const el = event.currentTarget
    if (!loading && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
      fetchPage(page + 1, true)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        disabled={disabled || !outletId}
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg border ${
          disabled || !outletId
            ? "border-neutral-200 text-neutral-400 cursor-not-allowed"
            : "border-neutral-300 text-neutral-700 hover:bg-neutral-50"
        }`}
      >
        <Plus className="w-4 h-4" stroke={1.5} />
        Tambah item luar PO
      </button>
    )
  }

  return (
    <div className="space-y-2 border border-neutral-200 rounded-xl p-3 bg-neutral-50">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-neutral-700">Pilih produk yang datang tapi tidak ada di PO</div>
        <button
          type="button"
          onClick={() => {
            setOpen(false)
            setQuery("")
          }}
          className="text-[11px] text-neutral-500 hover:text-neutral-800"
        >
          Tutup
        </button>
      </div>
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Cari SKU / Nama / Brand / Model..."
      />
      <div className="h-60 overflow-auto border rounded-xl bg-white" onScroll={onScroll}>
        {items.map((row) => {
          const id = row.product_unit_id ?? row.id
          const name = row.product_unit_long_name ?? row.product_unit ?? row.name ?? "-"
          const sku = row.sku ?? "-"
          const brand = row.product_brand ?? row.brand ?? "-"
          return (
            <button
              key={id}
              type="button"
              className="w-full px-3 py-2 text-left border-b last:border-b-0 hover:bg-neutral-50"
              onClick={() => {
                onPick({
                  product_unit_id: String(id),
                  product_unit_name: name,
                })
                setOpen(false)
                setQuery("")
              }}
            >
              <div className="text-sm font-medium text-neutral-900">{name}</div>
              <div className="text-xs text-neutral-500">{sku} • {brand}</div>
            </button>
          )
        })}
        {loading && <div className="p-3 text-xs text-neutral-500">Memuat...</div>}
        {!loading && items.length === 0 && (
          <div className="p-3 text-xs text-neutral-500">Tidak ada hasil</div>
        )}
      </div>
    </div>
  )
}

function todayISO() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// 230726 (permintaan user): qty dipesan & qty terima ditampilkan/diinput dalam
// UOM baris PO-nya. Server tetap menyimpan PCS — konversi dilakukan saat load
// (÷ faktor) dan saat kirim payload (× faktor, dibulatkan). Faktor kini dari
// MASTER satuan (LSN/PSG/GRS/…) via helpers/uom-helper; dimuat saat modul import
// supaya siap sebelum data GR tiba (fallback LUSIN/PASANG tetap ada).
loadMasterUomFactors()
const uomFactor = masterUomFactor

// 240726 (permintaan user): total qty cetak GR mengikuti UOM. Dalam SATU UOM,
// total pcs = Σ round(qty×faktor); total di UOM tsb = total pcs ÷ faktor
// (LUSIN: pcs÷12; PASANG: ÷2; PCS: ÷1 → tak ada rumus khusus). Rounding lewat
// pcs per baris → paritas dgn total dokumen PO (OrderController::displayQtyTotal).
function qtyTotalsByUom(items) {
  const pcsByUom = {}
  for (const it of items || []) {
    const q = num(it?.qty_received)
    if (q <= 0) continue
    const uom = String(it?.uom || "PCS").toUpperCase()
    const factor = uomFactor(uom) || 1
    pcsByUom[uom] = (pcsByUom[uom] || 0) + Math.round(q * factor)
  }
  return Object.entries(pcsByUom).map(([uom, pcs]) => {
    const factor = uomFactor(uom) || 1
    return { uom, qty: Math.round((pcs / factor) * 100) / 100 }
  })
}

const fmtQtyId = (n) =>
  new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(Number(n) || 0)

// "5 LUSIN • 10 PCS" (per UOM). "0" bila tak ada item ber-qty.
function qtyTotalsLabel(items) {
  const rows = qtyTotalsByUom(items)
  if (!rows.length) return "0"
  return rows.map((r) => `${fmtQtyId(r.qty)} ${r.uom}`).join(" • ")
}
const toUomQty = (pcs, factor) => Math.round((num(pcs) / (factor || 1)) * 100) / 100
const qtyUomFmt = (v) =>
  Number.isInteger(num(v))
    ? String(num(v))
    : num(v).toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// 230726 (permintaan user): map baris item GR dari API + qty_ordered (Qty Dipesan)
// dari PO item, keduanya dalam UOM baris PO. Item luar PO = PCS, tanpa qty dipesan.
function mapGrItemRows(rows) {
  return (rows || [])
    .map((r) => {
      const poItem = r?.po_item ?? r?.poItem ?? null
      const uom = String(poItem?.uom || "PCS").toUpperCase()
      const factor = uomFactor(uom)
      return {
        id: r?.id,
        po_item_id: r?.po_item_id ?? null,
        product_unit: r?.product_unit?.name,
        product_unit_id: String(r?.product_unit_id ?? ""),
        uom,
        qty_received: toUomQty(r?.qty_received, factor),
        qty_ordered: poItem ? toUomQty(poItem.qty_ordered, factor) : null,
        // 230726 (permintaan user): qty yang SUDAH MASUK (kumulatif GR ter-posting
        // sebelumnya, dari po_item.qty_received) — tampil sebagai kolom info.
        qty_already_received: poItem ? toUomQty(poItem.qty_received, factor) : null,
        batch_no: r?.batch_no ?? "",
        expired_date: r?.expired_date ? String(r.expired_date).slice(0, 10) : "",
      }
    })
    .sort((a, b) => String(a.product_unit || "").localeCompare(String(b.product_unit || ""), undefined, { sensitivity: "base" }))
}

function moneyFmt(v) {
  const n = num(v)
  if (!n) return "-"
  return Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR" }).format(n)
}

async function apiFetch(path, { method = "GET", body } = {}) {
  const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
  const url = `${base}${path}`

  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : null,
  })

  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = json?.message || "Request gagal"
    throw new Error(msg)
  }
  return json
}

function fmtPrintDate(v) {
  if (!v) return "-"
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(d)
}

/**
 * Printable Goods Receipt document.
 * Hidden on screen, rendered only into the react-to-print iframe.
 */
const GoodsReceiptPrintDocument = forwardRef(function GoodsReceiptPrintDocument(
  {
    grNumber,
    status,
    poNumber,
    poId,
    outletName,
    supplierName,
    receiptDate,
    supplierDoNumber,
    remarks,
    items = [],
    totalQtyLabel = "0",
    printedBy,
  },
  ref
) {
  const printedAt = new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date())

  // 230726 (permintaan user): baris ber-qty terima 0 TIDAK ikut dicetak —
  // draft GR kini di-seed 0 (diisi bertahap), dokumen hanya memuat yang
  // benar-benar diterima.
  const printableItems = items.filter((it) => num(it.qty_received) > 0)

  const PAGE_SIZE = 20
  const chunks = []
  for (let i = 0; i < printableItems.length; i += PAGE_SIZE) {
    chunks.push(printableItems.slice(i, i + PAGE_SIZE))
  }
  if (chunks.length === 0) chunks.push([])

  const renderHeader = () => (
    <>
      <div className="flex items-start justify-between pb-4 border-b border-neutral-300">
        <div>
          <div className="text-xl font-bold tracking-wide">PENERIMAAN BARANG</div>
          <div className="text-[12px] text-neutral-600">Goods Receipt</div>
        </div>
        <div className="text-right">
          <div className="text-[13px] font-semibold">{grNumber || "-"}</div>
          <div className="text-[11px] text-neutral-600">
            Status: {String(status || "DRAFT").toUpperCase()}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 mt-4 gap-x-8 gap-y-1">
        <div className="flex">
          <div className="w-40 text-neutral-500">No. PO</div>
          <div className="flex-1 font-medium">{poNumber || poId || "-"}</div>
        </div>
        <div className="flex">
          <div className="w-40 text-neutral-500">Tanggal Terima</div>
          <div className="flex-1 font-medium">{fmtPrintDate(receiptDate)}</div>
        </div>
        <div className="flex">
          <div className="w-40 text-neutral-500">Outlet / Gudang</div>
          <div className="flex-1 font-medium">{outletName || "-"}</div>
        </div>
        <div className="flex">
          <div className="w-40 text-neutral-500">No. Surat Jalan</div>
          <div className="flex-1 font-medium">{supplierDoNumber || "-"}</div>
        </div>
        <div className="flex">
          <div className="w-40 text-neutral-500">Supplier</div>
          <div className="flex-1 font-medium">{supplierName || "-"}</div>
        </div>
      </div>
    </>
  )

  return (
    <div ref={ref} className="hidden print:block">
      <style>{`
        @page { size: A4; margin: 16mm 14mm; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .gr-page { page-break-after: always; }
          .gr-page:last-child { page-break-after: auto; }
        }
      `}</style>

      {chunks.map((chunkItems, pageIdx) => {
        const isLastPage = pageIdx === chunks.length - 1
        const baseIndex = pageIdx * PAGE_SIZE

        return (
          <div key={pageIdx} className="gr-page text-[12px] text-neutral-900">
            {renderHeader()}

            <table className="w-full mt-5 border-collapse">
              <thead>
                <tr className="bg-neutral-100">
                  <th className="w-8 px-2 py-1 text-left border border-neutral-300">No</th>
                  <th className="px-2 py-1 text-left border border-neutral-300">Produk</th>
                  <th className="w-24 px-2 py-1 text-left border border-neutral-300">Sumber</th>
                  <th className="w-20 px-2 py-1 text-right border border-neutral-300">Qty</th>
                  <th className="w-20 px-2 py-1 text-left border border-neutral-300">Satuan</th>
                  <th className="px-2 py-1 text-left border w-28 border-neutral-300">Batch</th>
                  <th className="px-2 py-1 text-left border w-28 border-neutral-300">Expired</th>
                </tr>
              </thead>
              <tbody>
                {chunkItems.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-2 py-3 text-center border border-neutral-300 text-neutral-500">
                      Tidak ada item
                    </td>
                  </tr>
                )}
                {chunkItems.map((it, i) => (
                  <tr key={it.product_unit_id || `${pageIdx}-${i}`}>
                    <td className="px-2 py-1 align-top border border-neutral-300">{baseIndex + i + 1}</td>
                    <td className="px-2 py-1 align-top border border-neutral-300">
                      {it.product_unit || it.product_unit_id}
                    </td>
                    <td className="px-2 py-1 align-top border border-neutral-300">
                      {it.po_item_id ? "PO" : "Luar PO"}
                    </td>
                    <td className="px-2 py-1 text-right align-top border border-neutral-300">
                      {Number(it.qty_received) || 0}
                    </td>
                    <td className="px-2 py-1 align-top border border-neutral-300">{it.uom || "PCS"}</td>
                    <td className="px-2 py-1 align-top border border-neutral-300">{it.batch_no || "-"}</td>
                    <td className="px-2 py-1 align-top border border-neutral-300">
                      {it.expired_date ? fmtPrintDate(it.expired_date) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
              {isLastPage ? (
                <tfoot>
                  <tr className="font-semibold bg-neutral-50">
                    <td colSpan={3} className="px-2 py-1 text-right border border-neutral-300">
                      Total Qty
                    </td>
                    {/* 240726 (permintaan user): total per UOM (LUSIN=pcs÷12, PASANG=÷2,
                        PCS=÷1) — bukan lagi total PCS gabungan. */}
                    <td colSpan={4} className="px-2 py-1 border border-neutral-300">{totalQtyLabel}</td>
                  </tr>
                </tfoot>
              ) : (
                <tfoot>
                  <tr>
                    <td colSpan={7} className="px-2 py-1 text-right border border-neutral-300 text-[11px] text-neutral-500 italic">
                      bersambung ke halaman berikutnya…
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>

            {isLastPage && remarks ? (
              <div className="mt-4">
                <div className="text-neutral-500">Catatan</div>
                <div className="mt-1 whitespace-pre-wrap border border-neutral-200 rounded p-2 min-h-[48px]">
                  {remarks}
                </div>
              </div>
            ) : null}

            {isLastPage ? (
              <div className="grid grid-cols-3 gap-6 mt-12 text-center">
                {["Pengirim", "Diterima oleh", "Mengetahui"].map((role) => (
                  <div key={role}>
                    <div className="text-neutral-600">{role}</div>
                    <div className="h-16" />
                    <div className="pt-1">( _______________ )</div>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="mt-8 pt-2 border-t border-neutral-200 flex items-center justify-between text-[10px] text-neutral-400">
              <span>
                {grNumber ? `${grNumber} • ` : ""}Dicetak{printedBy ? ` oleh ${printedBy}` : ""} pada {printedAt}
              </span>
              <span>Halaman {pageIdx + 1} dari {chunks.length}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
})

/**
 * Goods Receipt Form Page
 * Route example:
 * - Create (manual): /goods-receipts/create
 * - Edit: /goods-receipts/:id/edit
 *
 * Optional query:
 * - ?po_id=...  (prefill PO)
 */
export default function GoodsReceiptFormPage() {
  const { id } = useParams()
  const isEdit = Boolean(id)

  const { can, currentBranch, user } = useAuth()
  const { title, transition, setTransition, setTitle } = useOutletContext()
  const navigate = useNavigate()
  const location = useLocation()

  const branchSelection = getBranchList()
  const supplierSelection = getGenericSupplier()

  const qs = new URLSearchParams(location.search)
  const poIdFromQuery = qs.get("po_id") || null

  const [errors, setErrors] = useState({})
  // Guard double-submit: nonaktifkan tombol & tolak klik ganda saat request berjalan.
  const [submitting, setSubmitting] = useState(false)

  // Header
  const [grId, setGrId] = useState(null)
  const [grNumber, setGrNumber] = useState(null)
  const [status, setStatus] = useState("DRAFT")

  const [poId, setPoId] = useState(poIdFromQuery)
  const [poNumber, setPoNumber] = useState(null)
  const [outletId, setOutletId] = useState(currentBranch?.id ?? null)
  const [outletName, setOutletName] = useState(currentBranch?.name ?? null)
  const [supplierId, setSupplierId] = useState(null)
  const [supplierName, setSupplierName] = useState(null)

  const [receiptDate, setReceiptDate] = useState(todayISO())
  const [supplierDoNumber, setSupplierDoNumber] = useState("")
  const [remarks, setRemarks] = useState("")

  // Items (manual input mirip PO)
  const [items, setItems] = useState([]) // {id?, po_item_id?, product_unit_id, qty_received, batch_no, expired_date}
  const [productUnitIdInput, setProductUnitIdInput] = useState("")
  const [qtyInput, setQtyInput] = useState(1)
  const [batchInput, setBatchInput] = useState("")
  const [expiredInput, setExpiredInput] = useState("")

  // 270726 (permintaan user): cari item — sama seperti form Pembelian. Satu GR bisa
  // berisi puluhan produk; kotak ini HANYA menyaring tampilan (state item utuh),
  // jadi qty yang sudah diisi tidak hilang saat kata kunci berubah.
  const [itemSearch, setItemSearch] = useState("")
  const itemMatches = (it) => {
    const q = itemSearch.trim().toLowerCase()
    if (!q) return true
    return String(it.product_unit || "").toLowerCase().includes(q)
      || String(it.product_unit_id || "").toLowerCase().includes(q)
  }

  const isDraft = String(status || "").toUpperCase() === "DRAFT"
  const isLocked = !isDraft

  // Print PDF (client-side, react-to-print)
  const printRef = useRef()
  const handlePrint = useReactToPrint({
    documentTitle: `Penerimaan Barang ${grNumber || ""}`.trim(),
    removeAfterPrint: true,
  })
  const onPrint = () => {
    // 230726: baris qty terima 0 tidak ikut dicetak — blok print bila tak ada
    // satu pun item yang qty terimanya terisi.
    if (!items.some((it) => num(it.qty_received) > 0)) {
      return toast.error("Tidak ada item ber-qty terima untuk dicetak")
    }
    handlePrint(null, () => printRef.current)
  }

  useEffect(() => {
    setTitle?.(isEdit ? "Edit Penerimaan Barang" : "Buat Penerimaan Barang")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit])

  useEffect(() => {
    if (transition?.reverse) setTransition?.({ type: "slide", reverse: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load GR if edit
  useEffect(() => {
    const run = async () => {
      if (!isEdit) return
      try {
        nProgress.start()
        const res = await apiFetch(`/goods-receipts/${id}`)
        const gr = res?.data || res

        setGrId(gr?.id ?? id)
        setGrNumber(gr?.gr_number ?? null)
        setStatus(gr?.status ?? "DRAFT")

        setPoId(gr?.po_id ?? null)
        setOutletId(gr?.outlet_id ?? null)
        setReceiptDate((gr?.receipt_date || "").slice(0, 10) || todayISO())
        setSupplierDoNumber(gr?.supplier_do_number ?? "")
        setRemarks(gr?.remarks ?? "")

        const po = gr?.purchase_order ?? gr?.purchaseOrder ?? null
        setPoNumber(po?.po_number ?? null)
        setSupplierId(po?.supplier_id ?? null)

        const supName =
          po?.supplier_name_snapshot ||
          po?.suppliers?.name ||
          supplierSelection?.find((x) => String(x.id) === String(po?.supplier_id))?.name ||
          null
        setSupplierName(supName)

        const b = branchSelection?.find((x) => String(x.id) === String(gr?.outlet_id))
        setOutletName(b?.name ?? null)

        // supplier_id tidak ada di GR model kamu (di service gak disimpan).
        // biasanya supplier bisa diambil dari PO details (kalau UI butuh).
        // Untuk sementara: keep supplier selection manual.
        // Kalau kamu punya endpoint PO show, bisa fetch PO untuk ambil supplier_id & name.

        const rows = Array.isArray(gr?.items) ? gr.items : []
        setItems(mapGrItemRows(rows))
      } catch (e) {
        toast.error(e.message || "Gagal memuat GR")
      } finally {
        nProgress.done()
      }
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id])

  const totals = useMemo(() => {
    // 230726: qty per baris dalam UOM masing-masing — total PCS utk kebutuhan internal.
    const qtyTotal = items.reduce((acc, r) => acc + Math.round(num(r.qty_received) * uomFactor(r.uom)), 0)
    // 240726: total tampilan mengikuti UOM (LUSIN=pcs÷12, PASANG=÷2, PCS=÷1).
    const label = qtyTotalsLabel(items)
    return { qtyTotal, label }
  }, [items])

  const validate = () => {
    const e = {}
    if (!poId) e.po_id = "PO ID wajib diisi"
    if (!outletId) e.outlet_id = "Outlet wajib dipilih"
    if (!receiptDate) e.receipt_date = "Tanggal terima wajib diisi"
    if (!items.length) e.items = "Minimal 1 item"

    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      if (!it.product_unit_id) e[`items.${i}.product_unit_id`] = "Product unit wajib diisi"
      // 220726 (permintaan user): draft boleh qty 0 (default seed 0); negatif ditolak.
      // 230726 (permintaan user): penjagaan "harus > 0" utk posting juga DIHAPUS —
      // baris qty 0 dilewati backend saat post.
      if (num(it.qty_received) < 0) e[`items.${i}.qty_received`] = "Qty terima tidak boleh negatif"
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  // 230726 (permintaan user): salin qty dipesan (PO) ke qty diterima untuk semua
  // baris ber-sumber PO — dengan konfirmasi dulu (tidak langsung apply). Item luar
  // PO (tanpa qty dipesan) tidak disentuh.
  const onCopyOrderedQty = () => {
    if (!items.some((it) => it.qty_ordered != null)) {
      toast.error("Tidak ada qty dipesan untuk dicopy")
      return
    }
    if (!window.confirm("Copy qty dipesan ke qty diterima untuk semua item PO? Qty diterima yang sudah terisi akan ditimpa.")) return
    setItems((prev) => prev.map((it) => (it.qty_ordered != null ? { ...it, qty_received: it.qty_ordered } : it)))
    toast.success("Qty dipesan dicopy ke qty diterima")
  }

  const buildHeaderPayload = () => ({
    receipt_date: receiptDate,
    supplier_do_number: supplierDoNumber || null,
    remarks: remarks || null,
  })

  const buildItemsPayload = () => ({
    allow_delete_missing: true,
    items: items.map((it) => ({
      id: it.id || undefined,
      po_item_id: it.po_item_id || undefined,
      product_unit_id: it.product_unit_id,
      product_unit: it.product_unit,
      // 230726: input dalam UOM baris PO → server tetap menerima PCS (bulat).
      qty_received: Math.round(num(it.qty_received) * uomFactor(it.uom)),
      batch_no: it.batch_no || null,
      expired_date: it.expired_date || null,
    })),
  })

  const onAddItem = () => {
    const pid = String(productUnitIdInput || "").trim()
    const qty = num(qtyInput || 0)

    if (!pid) return toast.error("Isi product unit dulu")
    if (qty <= 0) return toast.error("Qty terima harus > 0")

    const existsIndex = items.findIndex((x) => String(x.product_unit_id) === pid)

    if (existsIndex >= 0) {
      const next = [...items]
      next[existsIndex] = {
        ...next[existsIndex],
        qty_received: num(next[existsIndex].qty_received) + qty,
        batch_no: batchInput || next[existsIndex].batch_no,
        expired_date: expiredInput || next[existsIndex].expired_date,
      }
      setItems(next)
      toast.success("Qty item ditambahkan")
    } else {
      setItems((prev) => [
        ...prev,
        {
          product_unit_id: pid,
          qty_received: qty,
          batch_no: batchInput || "",
          expired_date: expiredInput || "",
        },
      ])
      toast.success("Item ditambahkan")
    }

    setProductUnitIdInput("")
    setQtyInput(1)
    setBatchInput("")
    setExpiredInput("")
  }

  const onRemoveItem = (product_unit_id) => {
    setItems((prev) => prev.filter((x) => String(x.product_unit_id) !== String(product_unit_id)))
  }

  const onAddNonPoItem = (picked) => {
    const pid = String(picked?.product_unit_id || "").trim()
    if (!pid) return
    const existsIndex = items.findIndex((x) => String(x.product_unit_id) === pid)
    if (existsIndex >= 0) {
      toast.error("Produk ini sudah ada di daftar item")
      return
    }
    setItems((prev) => [
      ...prev,
      {
        po_item_id: null,
        product_unit_id: pid,
        product_unit: picked?.product_unit_name || pid,
        uom: "PCS",
        qty_received: 1,
        batch_no: "",
        expired_date: "",
      },
    ])
    toast.success("Item luar PO ditambahkan")
  }

  const onUpdateItem = (product_unit_id, patch) => {
    setItems((prev) =>
      prev.map((it) =>
        String(it.product_unit_id) === String(product_unit_id) ? { ...it, ...patch } : it
      )
    )
  }

  /**
   * SAVE DRAFT:
   * - If create: POST /goods-receipts
   * - If edit: PUT header + PUT items
   */
  const onSaveDraft = async () => {
    if (submitting) return
    if (!validate()) return

    try {
      setSubmitting(true)
      nProgress.start()

      if (!isEdit) {
        // create draft
        const payload = {
          po_id: poId,
          outlet_id: outletId,
          receipt_date: receiptDate,
          supplier_do_number: supplierDoNumber || null,
          remarks: remarks || null,
          items: buildItemsPayload().items,
        }

        const res = await apiFetch(`/goods-receipts`, { method: "POST", body: payload })
        const newId = res?.data?.id || res?.id

        toast.success(res?.message || "Draft GR berhasil dibuat")

        if (newId) {
          navigate(`/goods-receipt/${newId}/edit`, {
            replace: true,
            state: { back: title, from: location?.pathname, transition: "slide" },
          })
        }
      } else {
        // update header then items (draft only)
        await apiFetch(`/goods-receipts/${id}/header`, { method: "PUT", body: buildHeaderPayload() })
        const resItems = await apiFetch(`/goods-receipts/${id}/items`, { method: "PUT", body: buildItemsPayload() })

        toast.success("Draft GR berhasil disimpan")
        // refresh state items if backend adjusts. Response updateItems belum tentu
        // membawa relasi po_item — qty_ordered lama dipertahankan per product_unit_id.
        const gr = resItems?.data || null
        if (gr?.items) {
          const prevOrdered = new Map(items.map((x) => [String(x.product_unit_id), x.qty_ordered]))
          const prevAlready = new Map(items.map((x) => [String(x.product_unit_id), x.qty_already_received]))
          setItems(
            mapGrItemRows(gr.items).map((row) => ({
              ...row,
              qty_ordered: row.qty_ordered ?? prevOrdered.get(row.product_unit_id) ?? null,
              qty_already_received: row.qty_already_received ?? prevAlready.get(row.product_unit_id) ?? null,
            }))
          )
        }
      }
    } catch (e) {
      toast.error(e.message || "Gagal menyimpan GR")
    } finally {
      setSubmitting(false)
      nProgress.done()
    }
  }

  const onPost = async () => {
    if (submitting) return
    if (!isEdit) return toast.error("Simpan draft dulu sebelum Post")
    if (!validate()) return

    if (!window.confirm(`Posting Penerimaan Barang ${grNumber || ""}?`)) return

    try {
      setSubmitting(true)
      nProgress.start()

      // ensure latest save before posting
      await apiFetch(`/goods-receipts/${id}/header`, { method: "PUT", body: buildHeaderPayload() })
      await apiFetch(`/goods-receipts/${id}/items`, { method: "PUT", body: buildItemsPayload() })

      const res = await apiFetch(`/goods-receipts/${id}/post`, { method: "POST", body: { postedBy: user?.name } })
      toast.success(res?.message || "GR berhasil di-post")
      setStatus("POSTED")
    } catch (e) {
      toast.error(e.message || "Gagal post GR")
    } finally {
      setSubmitting(false)
      nProgress.done()
    }
  }

  const onCancel = async () => {
    if (!isEdit) return
    const reason = window.prompt("Alasan pembatalan (opsional):", "")
    if (reason === null) return

    try {
      nProgress.start()
      const res = await apiFetch(`/goods-receipts/${id}/cancel`, {
        method: "POST",
        body: { reason: reason || null, cancelledBy: user?.name },
      })
      toast.success(res?.message || "GR berhasil dibatalkan")
      setStatus("CANCELLED")
    } catch (e) {
      toast.error(e.message || "Gagal membatalkan GR")
    } finally {
      nProgress.done()
    }
  }

  return (
    <div className="pb-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">
            {isEdit ? "Edit Penerimaan Barang" : "Buat Penerimaan Barang"}{" "}
            <span className="text-xs font-medium text-neutral-500">(GR)</span>
          </h1>
          <p className="text-xs text-neutral-500">
            Alur: PO Approved ➜ Draft GR ➜ Post (update qty terima item PO).
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/goods-receipt"
            className="px-4 py-3 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl hover:bg-neutral-50"
            state={{ back: title, from: location?.pathname, transition: "slide" }}
          >
            Kembali
          </Link>

          {isEdit && (
            <button
              type="button"
              onClick={onPrint}
              className="inline-flex items-center gap-2 px-4 py-3 text-xs font-semibold border text-neutral-700 border-neutral-200 rounded-xl hover:bg-neutral-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-neutral-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9V2h12v7" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <path d="M6 14h12v8H6z" />
              </svg>
              Print PDF
            </button>
          )}

          {/* {can?.("goods-receipt.cancel") && isEdit && status !== "CANCELLED" && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-3 text-xs font-semibold text-white bg-red-600 rounded-xl hover:bg-red-500"
            >
              Cancel
            </button>
          )} */}

          {can?.("goods-receipt.post") && isEdit && String(status).toUpperCase() === "DRAFT" && (
            <button
              type="button"
              onClick={onPost}
              disabled={submitting}
              className="px-4 py-3 text-xs font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-500 disabled:bg-neutral-400 disabled:cursor-not-allowed"
            >
              Post
            </button>
          )}

          {(can?.("goods-receipt.create") || can?.("goods-receipt.update")) && (
            <button
              type="button"
              onClick={onSaveDraft}
              disabled={(isLocked && isEdit) || submitting}
              className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                (isLocked && isEdit) || submitting ? "bg-neutral-400 cursor-not-allowed" : "bg-brand-900 hover:bg-brand-800"
              }`}
            >
              Simpan Draft
            </button>
          )}
        </div>
      </div>

      {/* Main Form */}
      <div className="border border-neutral-200 rounded-xl">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-neutral-700">Nomor GR</label>
              <Input disabled value={grNumber || "Otomatis dibuat sistem"} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">{lang.status}</label>
              <Input disabled value={status || "DRAFT"} />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">
                PO ID<sup className="text-xs text-red-700"> *</sup>
              </label>
              <input
                disabled={isEdit} // po_id jangan berubah setelah create
                value={poId || ""}
                onChange={(e) => setPoId(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                placeholder="UUID / ID PO"
              />
              <ErrorMessage error={errors.po_id} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                {lang.outlet_name}<sup className="text-xs text-red-700"> *</sup>
              </label>
              <SelectSearchable
                disabled={isEdit} // outlet mengikuti PO (di service strict)
                filter={(value) => value.name}
                onChange={(value) => {
                  const b = branchSelection?.find((x) => String(x.id) === String(value))
                  setOutletId(value)
                  setOutletName(b?.name ?? null)
                }}
                selection={branchSelection}
                isLoading={false}
                value={outletId}
                keyValue={(value) => value.id}
                title={(value) => value.name}
                placeholder="-- Pilih Outlet --"
              />
              <ErrorMessage error={errors.outlet_id} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">Supplier</label>
              <SelectSearchable
                disabled={true}
                filter={(value) => value.name}
                onChange={(value) => {
                  setSupplierId(value)
                  const s = supplierSelection?.find((x) => String(x.id) === String(value))
                  setSupplierName(s?.name ?? null)
                }}
                selection={supplierSelection}
                isLoading={false}
                value={supplierId}
                keyValue={(value) => value.id}
                title={(value) => value.name}
                placeholder={supplierName ? supplierName : "-- (Opsional) dari PO --"}
              />
              <div className="text-[11px] text-neutral-500 mt-1">
                * Supplier idealnya diambil dari detail PO (kalau perlu, bisa ditambahkan fetch PO untuk hydrate supplier).
              </div>
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                Tanggal Terima<sup className="text-xs text-red-700"> *</sup>
              </label>
              <input
                type="date"
                disabled={isLocked && isEdit}
                value={receiptDate}
                onChange={(e) => setReceiptDate(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
              <ErrorMessage error={errors.receipt_date} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">Nomor Surat Jalan Supplier</label>
              <input
                disabled={isLocked && isEdit}
                value={supplierDoNumber}
                onChange={(e) => setSupplierDoNumber(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                placeholder="SJ-001 / DO-xxx"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">{lang.notes}</label>
              <textarea
                disabled={isLocked && isEdit}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="border border-neutral-200 rounded-xl">
        <div className="px-4 py-5 sm:p-6 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-neutral-900">Item</div>
              {/* <div className="text-xs text-neutral-500">
                Input: product_unit_id + qty_received + batch_no + expired_date.
              </div> */}
            </div>
            <div className="flex items-center gap-3">
              {!(isEdit && isLocked) && (
                <button
                  type="button"
                  onClick={onCopyOrderedQty}
                  className="px-3 py-2 text-[11px] font-semibold text-white bg-brand-800 rounded-lg hover:bg-brand-600"
                >
                  Copy Qty Dipesan → Qty Terima
                </button>
              )}
              <div className="text-xs text-neutral-600">
                Total Qty: <b>{totals.label}</b>
              </div>
            </div>
          </div>

          <ErrorMessage error={errors.items} />

          {/* Tambah item luar PO */}
          <NonPoProductPicker
            disabled={isEdit && isLocked}
            outletId={outletId}
            onPick={onAddNonPoItem}
          />

          {/* Add controls */}
          {/* <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-4">
              <label className="block text-xs text-neutral-700">Product Unit ID</label>
              <input
                disabled={isEdit && isLocked}
                value={productUnitIdInput}
                onChange={(e) => setProductUnitIdInput(e.target.value)}
                className="block w-full px-3 py-2 mt-1 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                placeholder="PRU_..."
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">Qty Received</label>
              <input
                type="number"
                min={1}
                disabled={isEdit && isLocked}
                value={qtyInput}
                onChange={(e) => setQtyInput(e.target.value)}
                className="block w-full px-3 py-2 mt-1 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
            </div>

            <div className="col-span-3">
              <label className="block text-xs text-neutral-700">Batch No</label>
              <input
                disabled={isEdit && isLocked}
                value={batchInput}
                onChange={(e) => setBatchInput(e.target.value)}
                className="block w-full px-3 py-2 mt-1 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                placeholder="B001"
              />
            </div>

            <div className="col-span-3">
              <label className="block text-xs text-neutral-700">Expired Date</label>
              <input
                type="date"
                disabled={isEdit && isLocked}
                value={expiredInput}
                onChange={(e) => setExpiredInput(e.target.value)}
                className="block w-full px-3 py-2 mt-1 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
            </div>

            <div className="col-span-12">
              <button
                type="button"
                disabled={isEdit && isLocked}
                onClick={onAddItem}
                className={`w-full inline-flex justify-center items-center gap-2 px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                  isEdit && isLocked ? "bg-neutral-400 cursor-not-allowed" : "bg-brand-900 hover:bg-brand-800"
                }`}
              >
                <Plus className="w-4 h-4" stroke={1.5} />
                Add Item
              </button>
            </div>
          </div> */}

          {/* 270726 (permintaan user): cari item (nama / kode produk) — pola sama
              dengan form Pembelian; satu GR bisa berisi banyak produk. */}
          <div className="mb-3">
            <input
              type="text"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder="Cari item (nama / kode produk)…"
              className="w-full px-3 py-2 text-xs border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800 sm:w-80"
            />
            {itemSearch.trim() && (
              <span className="ml-2 text-[11px] text-neutral-500">
                {items.filter(itemMatches).length} dari {items.length} item
              </span>
            )}
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto border border-neutral-200 rounded-xl">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Product Unit</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Sumber</th>
                  <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500">Qty Dipesan</th>
                  <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500">Qty Sudah Masuk</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty Terima</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Satuan</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Batch</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Expired</th>
                  <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500">Aksi</th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-neutral-200">
                {items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-xs text-center text-neutral-500">
                      Belum ada item
                    </td>
                  </tr>
                )}

                {items.length > 0 && items.filter(itemMatches).length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-xs text-center text-neutral-500">
                      Tidak ada item cocok dengan pencarian "{itemSearch.trim()}"
                    </td>
                  </tr>
                )}

                {items.map((it, idx) => {
                  // Baris disembunyikan lewat return null (BUKAN filter) supaya `idx`
                  // tetap menunjuk posisi asli di state — dipakai handler qty & key error.
                  if (!itemMatches(it)) return null
                  const isNonPo = !it.po_item_id
                  return (
                  <tr key={it.product_unit_id} className="hover:bg-brand-50 transition-colors">
                    <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                      {it.product_unit || it.product_unit_id}
                      {errors[`items.${idx}.product_unit_id`] && (
                        <div className="mt-1 text-[11px] text-red-600">{errors[`items.${idx}.product_unit_id`]}</div>
                      )}
                    </td>

                    <td className="px-3 py-4 text-xs whitespace-nowrap">
                      {isNonPo ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-800">
                          Luar PO
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-neutral-100 text-neutral-700">
                          PO
                        </span>
                      )}
                    </td>

                    <td className="px-3 py-4 text-xs text-right text-neutral-800 whitespace-nowrap">
                      {it.qty_ordered != null ? qtyUomFmt(it.qty_ordered) : "-"}
                    </td>

                    <td className="px-3 py-4 text-xs text-right text-neutral-800 whitespace-nowrap">
                      {it.qty_already_received != null ? qtyUomFmt(it.qty_already_received) : "-"}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={it.qty_received}
                        disabled={isEdit && isLocked}
                        onChange={(e) => onUpdateItem(it.product_unit_id, { qty_received: e.target.value })}
                        className="w-28 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                      />
                      {errors[`items.${idx}.qty_received`] && (
                        <div className="mt-1 text-[11px] text-red-600">{errors[`items.${idx}.qty_received`]}</div>
                      )}
                    </td>

                    <td className="px-3 py-4 text-xs font-medium text-neutral-700 whitespace-nowrap">
                      {it.uom || "PCS"}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      <input
                        value={it.batch_no || ""}
                        disabled={isEdit && isLocked}
                        onChange={(e) => onUpdateItem(it.product_unit_id, { batch_no: e.target.value })}
                        className="w-36 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                      />
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      <input
                        type="date"
                        value={it.expired_date || ""}
                        disabled={isEdit && isLocked}
                        onChange={(e) => onUpdateItem(it.product_unit_id, { expired_date: e.target.value })}
                        className="w-40 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                      />
                    </td>

                    <td className="px-3 py-4 text-xs text-right whitespace-nowrap">
                      {isNonPo && !(isEdit && isLocked) ? (
                        <button
                          type="button"
                          onClick={() => onRemoveItem(it.product_unit_id)}
                          className="px-3 py-2 text-[11px] font-semibold text-white rounded-lg bg-red-600 hover:bg-red-500"
                        >
                          Hapus
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-neutral-500">
            * Post akan menambah qty terima pada item PO dan update status PO (PARTIAL_RECEIVED / RECEIVED).
          </div>
        </div>
      </div>

      <GoodsReceiptPrintDocument
        ref={printRef}
        grNumber={grNumber}
        status={status}
        poNumber={poNumber}
        poId={poId}
        outletName={outletName}
        supplierName={supplierName}
        receiptDate={receiptDate}
        supplierDoNumber={supplierDoNumber}
        remarks={remarks}
        items={items}
        totalQtyLabel={totals.label}
        printedBy={user?.name}
      />
    </div>
  )
}
